import { ContentConsumerTransport } from '@modules/content-plan/services/contentConsumerTransport';
/**
 * ContentPlanApiClient — HTTP client for the content-plan pipeline.
 *
 * Mirrors AgentApiClient (content-agent) pattern — fire-and-forget POST
 * that returns { taskId, strategyId } immediately. Frontend then
 * subscribes to the backend_tasks Firestore doc via useBackendTask().
 */

import { getAuth } from 'firebase/auth';
import { emitBillingBlocked, parseBillingBlockedPayload } from '@modules/billing-access';
import type {
  PlatformId,
  ContentMixPreset,
  PillarMode,
  PrimaryGoal,
  ToneOfVoiceProfile,
  DesignSystemProfile,
  StrategyPlatformCadence,
} from '../types';

// ─── Request / Response contracts ──────────────────────────────────

export interface GenerateContentPlanRequest {
  projectId: string;
  userId: string;
  authorId: string;
  name: string;
  description?: string;

  dateStart: string;                // ISO YYYY-MM-DD
  dateEnd: string;
  timezone?: string;

  targetPlatforms: PlatformId[];
  postsPerWeekPerPlatform: Partial<Record<PlatformId, number>>;
  platformCadences?: StrategyPlatformCadence[];
  primaryGoal: PrimaryGoal;

  // Source data — same shape as onboarding strategy gen
  sourceData: unknown;              // HydratedSourceData
  sourceDataIds: {
    audienceIds: string[];
    taskIds: string[];
    studyCaseIds?: string[];
    reviewIds?: string[];
    ratingIds?: string[];
    bigNumberIds?: string[];
    insightIds?: string[];
  };
  insightsAnalysisId?: string;

  // Content setup
  contentMixPreset: ContentMixPreset;
  targetThemeDistribution?: Record<string, number>;
  // Phase C — `pillarMode` deprecated. TopicPillar is now a first-class entity
  // managed via the Pillar editor (Creator tab); pillar distribution is no
  // longer a strategy-create input. Kept optional for backwards-compat with
  // any callers still sending it; backend ignores when omitted.
  pillarMode?: PillarMode;

  // Copy-on-create profiles
  toneOfVoice?: ToneOfVoiceProfile;
  designSystem?: DesignSystemProfile;

  // Creator research opt-in (default false = no scraper cost)
  creatorResearch?: {
    enabled: boolean;
    seedHandles?: string[];
    niche?: string;
    maxCreators?: number;
    minFollowerCount?: number;
    maxPostsPerCreator?: number;
    cacheDays?: number;
  };

  // Auto-generate assets opt-in (default false — user reviews copy first)
  autoGenerateAssets?: boolean;

  // Provider/model
  provider?: string;
  models?: {
    strategy?: string;
    research?: string;
    platformPost?: string;
  };
  maxTokens?: number;
}

export interface GenerateContentPlanResponse {
  success: boolean;
  taskId: string;
  strategyId: string;
  error?: string;
}

export interface StrategyPreflightRequest {
  projectId: string;
  userId?: string;
  authorId: string;
  name?: string;
  dateStart: string;
  dateEnd: string;
  targetPlatforms: PlatformId[];
  postsPerWeekPerPlatform: Partial<Record<PlatformId, number>>;
  platformCadences?: StrategyPlatformCadence[];
  primaryGoal: PrimaryGoal;
  contentMixPreset: ContentMixPreset;
  autoFillBacklog?: boolean;
  autoAssignTopicsToSlots?: boolean;
}

export interface StrategyPreflightResponse {
  success: boolean;
  globalPostCount: number;
  sprintCount: number;
  coverage: {
    ok: boolean;
    gaps: Array<{ themeId: string; missingCount: number }>;
  };
  overallCoverage: number;
  estimatedTopicsToGenerate: number;
  effectiveFlags: {
    autoFillBacklog: boolean;
    autoAssignTopicsToSlots: boolean;
  };
  error?: string;
}

export interface PillarSourceReanalysisCurrentPillar {
  id: string;
  name?: string;
  summary?: string;
  description?: string;
  knowledgeContext?: string;
  keyTheses?: Array<{
    thesis: string;
    confidence?: number;
    sourcePostIds?: string[];
  }>;
  sourcePostIds?: string[];
  sourceHighlights?: Array<{
    postId: string;
    platformId?: string;
    quoteOrParaphrase: string;
    relevance: string;
  }>;
  guardrails?: string[];
  authorStance?: string;
  funnelStage?: string;
  weight?: number;
}

export interface PillarSourceReanalysisProposal {
  summary?: string;
  description?: string;
  knowledgeContext?: string;
  keyTheses?: PillarSourceReanalysisCurrentPillar['keyTheses'];
  sourceHighlights?: PillarSourceReanalysisCurrentPillar['sourceHighlights'];
  guardrails?: string[];
  authorStance?: string;
  changeSummary: string;
  hasMaterialChanges?: boolean;
}

export type PillarSourceReanalysisUpdateMode = 'conservative' | 'balanced' | 'expansive';

export type ContentPlanAssetOwnerKind =
  | 'post_example'
  | 'post'
  | 'content_post'
  | 'content_post_draft'
  | 'sprint_post';

// ─── Client ────────────────────────────────────────────────────────

export interface ContentPlanApiClientOptions {
  baseUrl?: string;
  getAuthToken?: () => Promise<string | null>;
}

export class ContentPlanApiClient {
  private readonly baseUrl: string;
  private readonly getAuthToken?: () => Promise<string | null>;

  constructor(opts: ContentPlanApiClientOptions = {}) {
    // baseUrl resolution (mirror AgentApiClient pattern):
    //   • Cloud build (deployBlueGreen sed-overrides .env.{env}):
    //     VITE_AGENT_API_URL = https://{env}-api.segmently.ai → GLB → /api/agent/*
    //     → builder-service Cloud Run.
    //   • Local LOCAL_WEB_SHELL=true: .env.dev sets VITE_AGENT_API_URL =
    //     http://localhost:8081 → direct hit on builder-service (bypasses
    //     edge-gateway, but gateway is just a proxy for /api/agent/* anyway,
    //     and builder-service CORS allows all origins).
    //   • Tests / Playwright: pass opts.baseUrl explicitly.
    //
    // Empty fallback ('') keeps relative-URL behaviour for environments where
    // frontend is served from the same origin as the API (rare but supported).
    this.baseUrl =
      opts.baseUrl ||
      import.meta.env.VITE_AGENT_API_URL ||
      import.meta.env.VITE_BUILDER_API_URL ||
      '';
    this.getAuthToken = opts.getAuthToken;
  }

  async saveStrategyRecord(projectId: string, strategy: Record<string, any>): Promise<void> {
    return new ContentConsumerTransport(this.request.bind(this)).saveStrategy(projectId, strategy);
  }

  async patchTopicRecord(projectId: string, topicId: string, authorId: string, patch: Record<string, unknown>): Promise<void> {
    return new ContentConsumerTransport(this.request.bind(this)).patchTopic(projectId, topicId, authorId, patch);
  }

  /**
   * @deprecated Retired in v2 staged pipeline (Sprint 10). Use
   * {@link generateTopicsBatch}, {@link createStrategy}, {@link generatePosts}
   * instead — each owns one stage.
   */
  async generateContentPlan(
    _request: GenerateContentPlanRequest,
  ): Promise<GenerateContentPlanResponse> {
    throw new Error(
      'POST /api/agent/generate-content-plan was retired in v2 staged pipeline. ' +
      'Use generateTopicsBatch() / createStrategy() / generatePosts() on this client instead.',
    );
  }

  // ─── Stage A — Topic Backlog ─────────────────────────────────────

  async generateTopicsBatch(request: {
    brief: {
      id?: string;
      projectId: string;
      authorId?: string;
      userId?: string;
      count: number;
      themeIds?: string[];
      themeDistribution?: Record<string, number>;
      userDescription?: string;
      userKeywords?: string[];
      userAvoidTopics?: string[];
      researchMode?: 'none' | 'use_existing' | 'run_fresh' | 'hybrid';
      existingResearchPackIds?: string[];
      runFreshResearch?: { queries?: string[]; sources?: unknown[]; maxResultsPerQuery?: number };
      sprintId?: string;
    };
    sourceData: unknown;
    sourceDataIds?: {
      audienceIds?: string[];
      taskIds?: string[];
      studyCaseIds?: string[];
      reviewIds?: string[];
      ratingIds?: string[];
      bigNumberIds?: string[];
      insightIds?: string[];
    };
    themeCatalog?: unknown[];
    nichePlaybook?: unknown;
    models?: { topicGenerator?: string; research?: string };
    searchProvider?: 'auto' | 'perplexity' | 'gemini' | 'openai';
    researchDepth?: 'standard' | 'deep';
    reasoningEffort?: 'low' | 'medium' | 'high';
    provider?: string;
    /**
     * Phase B — UI-driven pillar selection. Three semantics:
     *   undefined  → orchestrator auto-loads all confirmed pillars
     *   []         → opt-out (skip pillarIds emission in topics)
     *   [...ids]   → restrict to subset
     */
    selectedPillarIds?: string[];
    /** Phase B — include author identity context. Default true (backend). */
    includeAuthor?: boolean;
    authorId?: string;
  }): Promise<{ success: boolean; taskId: string; briefId?: string; error?: string }> {
    return this.post('/api/agent/content-plan/generate-topics-batch', request);
  }

  // ─── Stage B — Strategy planning ─────────────────────────────────

  async createStrategy(
    request: GenerateContentPlanRequest & {
      autoFillBacklog?: boolean;
      autoFillMaxCount?: number;
      autoAssignTopicsToSlots?: boolean;
      reactiveSlotPercent?: number;
      /**
       * M3 Run-all chain (B1): after strategy creation the backend
       * fire-and-forgets post generation for the FIRST sprint. The chained
       * task id lands in the strategy task result as
       * `chainedFirstSprintTaskId`.
       */
      generateFirstSprintPosts?: boolean;
      /** D1 default 'draft_review' — drafts pause for review unless auto mode. */
      firstSprintApplyMode?: 'draft_review' | 'auto_apply_topic' | 'auto_apply_sprint';
    },
  ): Promise<{ success: boolean; taskId: string; strategyId: string; error?: string }> {
    return this.post('/api/agent/content-plan/create-strategy', request);
  }

  async strategyPreflight(request: StrategyPreflightRequest): Promise<StrategyPreflightResponse> {
    return this.post('/api/agent/content-plan/strategy-preflight', request);
  }

  // ─── workflow-golden-tower — draft / approve gate ────────────────

  /**
   * B1: create strategy draft with approvalStage='draft_pending_approval'.
   * Returns the new strategyId so the frontend can navigate to
   * /project/:projectId/content-plan/strategy/:strategyId/review.
   */
  async createStrategyDraft(
    request: GenerateContentPlanRequest & {
      selectedAudienceBriefRefs?: Array<{
        analysisId: string;
        audienceId: string;
        sourcePath: string;
        labelSnapshot?: string;
      }>;
      audienceCoveragePolicy?: 'advisory' | 'strict';
    },
  ): Promise<{ success: boolean; taskId: string; strategyId: string; error?: string }> {
    return this.post('/api/agent/content-plan/strategies/draft', request);
  }

  /**
   * Edit a draft strategy. Supports whole-array replacement (strategicAngle,
   * sprints[i].outputThemeQuotas, sprints[i].outputGoalQuotas) AND scalar
   * dot-notation (sprints.{sprintId}.primaryAudienceBriefRef, etc.).
   */
  async patchStrategyDraft(
    strategyId: string,
    request: { projectId: string; authorId: string; patch: Record<string, unknown> },
  ): Promise<{ success: boolean; error?: string }> {
    return this.patch(`/api/agent/content-plan/strategies/${strategyId}/draft`, request);
  }

  /**
   * B2: approve a draft strategy. Always runs B2. Under 'strict' policy,
   * if post-planning violations remain, the task surfaces them and
   * approvalStage stays at 'draft_pending_approval' for the user to
   * resolve and retry.
   */
  async approveStrategy(
    strategyId: string,
    request: {
      projectId: string;
      userId: string;
      authorId: string;
      forceRestart?: boolean;
      models?: { strategy?: string };
    },
  ): Promise<{
    success: boolean;
    taskId?: string;
    error?: string;
    errorCode?: string;
    violationsBySprintId?: Record<string, unknown[]>;
  }> {
    return this.requestJsonAllowHttpError(
      'POST',
      `/api/agent/content-plan/strategies/${strategyId}/approve`,
      request,
    );
  }

  /**
   * Re-run B1 against the same audience refs. preserveUserEdits is
   * accepted for future use; v1 just re-issues the draft.
   */
  async regenerateStrategyDraft(
    strategyId: string,
    request: { projectId: string; authorId: string; userId?: string; preserveUserEdits?: boolean },
  ): Promise<{ success: boolean; taskId?: string; error?: string }> {
    return this.post(`/api/agent/content-plan/strategies/${strategyId}/regenerate-draft`, request);
  }

  /**
   * Per-sprint gap-fill — dispatches TopicBacklogOrchestrator with the
   * sprint's primaryAudienceBriefRef threaded onto every emitted topic.
   */
  async generateTopicsForSprint(
    strategyId: string,
    sprintId: string,
    request: { projectId: string; userId: string; authorId: string; themeIds?: string[]; count?: number },
  ): Promise<{ success: boolean; taskId: string; error?: string }> {
    return this.post(
      `/api/agent/content-plan/strategies/${strategyId}/sprints/${sprintId}/generate-topics`,
      request,
    );
  }

  /**
   * Directive theme creation — no AI call. Validates audience binding +
   * dataUsage, then appends to projects/{pid}/catalogs/theme_catalog.themes[]
   * via arrayUnion with kind='user_manual', source='user_confirmed'.
   */
  async createManualTheme(request: {
    projectId: string;
    userId: string;
    name: string;
    description?: string;
    audienceFit?: Array<{
      audienceBriefRef: { analysisId: string; audienceId: string; sourcePath: string; labelSnapshot?: string };
      fit: 'strong' | 'medium' | 'weak';
      rationale: string;
      dataUsage?: unknown;
    }>;
    appliesToAllAudiences?: boolean;
    appliesToAllAudiencesRationale?: string;
    dataUsage: { items: Array<{ sourcePath: string; value?: string; justification?: string }> };
  }): Promise<{ success: boolean; themeId: string; error?: string }> {
    return this.post('/api/agent/content-plan/themes/manual', request);
  }

  /**
   * Directive topic creation — no AI call. Writes to
   * projects/{pid}/topic_backlog/{topicId} with kind='user_manual'.
   */
  async createManualTopic(request: {
    projectId: string;
    userId: string;
    authorId: string;
    angle: string;
    keyMessage?: string;
    summary?: string;
    themeAssignment?: Array<{ themeId: string; weight?: number }>;
    primaryPillarId?: string;
    audienceFit?: Array<{
      audienceBriefRef: { analysisId: string; audienceId: string; sourcePath: string; labelSnapshot?: string };
      fit: 'strong' | 'medium' | 'weak';
      rationale: string;
      dataUsage?: unknown;
    }>;
    appliesToAllAudiences?: boolean;
    appliesToAllAudiencesRationale?: string;
    dataUsage: { items: Array<{ sourcePath: string; value?: string; justification?: string }> };
  }): Promise<{ success: boolean; topicId: string; error?: string }> {
    return this.post('/api/agent/content-plan/topics/manual', request);
  }

  // ─── Phase A — Author onboarding ────────────────────────────────

  /**
   * POST /api/agent/content-plan/author/onboard
   *
   * Fire-and-forget author onboarding.
   * Backend creates `backend_tasks/{taskId}` doc with taskType='author-onboarding',
   * runs AuthorOnboardingHandler (6 phases) in background, returns {taskId}.
   * UI subscribes via useBackendTask(taskId) for real-time progress.
   *
   * Modes:
   *   - 'url' — scraper-capable URL onboarding from profileUrl
   *   - 'manual' — uses manualInput.aboutText + manualInput.postTexts[]
   *   - 'redo-url' / 'redo-manual' — overwrites existing default author,
   *     preserving authorId (AC-072a)
   */
  async authorOnboard(request: {
    projectId: string;
    userId: string;
    /**
     * add-platform and scrape-profile-only support per-platform append and
     * metadata-only refresh respectively.
     */
    mode:
      | 'url'
      | 'manual'
      | 'redo-url'
      | 'redo-manual'
      | 'add-platform'
      | 'scrape-profile-only';
    profileUrl?: string;
    manualInput?: {
      platformId: string;
      aboutText?: string;
      displayName?: string;
      postTexts: string[];
    };
    targetAudienceIds: string[];
    targetAudienceContext?: Array<{ id: string; name?: string; description?: string }>;
    locale: string;
    existingAuthorId?: string;
    /** Force a new self-author instead of refreshing the baseline default author. */
    createNewSelfAuthor?: boolean;
    /** number | 'all' (schema 1.3.1+ — 'all' uses BD's dataset default cap) */
    maxPosts?: number | 'all';
    budgetCentsUsd?: number;
    model?: string;
    /**
     * Optional user-supplied focus for THIS analysis pass — spliced into
     * the AI prompt as a `## User-provided analysis focus` section.
     * Empty/whitespace → no-op.
     */
    analysisInstructions?: string;
  }): Promise<{ success: boolean; taskId?: string; error?: string }> {
    return this.post('/api/agent/content-plan/author/onboard', request);
  }

  /**
   * POST /api/agent/content-plan/author/onboard with mode='scrape-profile-only'
   *
   * Refresh profile metadata (followers, headline, avatar) on a specific
   * platform WITHOUT scraping posts or running AI mapping. Cheap operation —
   * one BD record per call (~0.15¢). Updates Platform.profileRaw / displayName
   * / avatarUrl on the existing platform doc.
   */
  async authorScrapeProfile(request: {
    projectId: string;
    userId: string;
    existingAuthorId: string;
    platformId: string;
    profileUrl: string;
  }): Promise<{ success: boolean; taskId?: string; error?: string }> {
    return this.authorOnboard({
      projectId: request.projectId,
      userId: request.userId,
      mode: 'scrape-profile-only',
      profileUrl: request.profileUrl,
      existingAuthorId: request.existingAuthorId,
      targetAudienceIds: [],
      locale: 'en',
      manualInput: { platformId: request.platformId, postTexts: [] },
    });
  }

  // ─── Phase G.0 — Partial-mode author endpoints ──────────────────

  /**
   * POST /api/agent/content-plan/author/scrape
   *
   * Re-scrape author corpus using existing platform configuration. Skips AI
   * mapping (voice/playbook/examples) — only the content corpus subcollection
   * is updated. Reuses `BackendTask` for progress.
   */
  async authorScrape(request: {
    projectId: string;
    userId: string;
    existingAuthorId: string;
    profileUrl?: string;
    targetAudienceIds?: string[];
    locale?: string;
    /** number | 'all' (schema 1.3.1+) — 'all' lifts the cap and lets BD return its dataset default. */
    maxPosts?: number | 'all';
    /**
     * Optional date-range (YYYY-MM-DD, inclusive). LinkedIn-only — honoured
     * by BrightData deep-posts dataset. Without these, BD returns the most
     * recent N posts (default sort).
     */
    scrapeSince?: string;
    scrapeUntil?: string;
  }): Promise<{ success: boolean; taskId?: string; error?: string }> {
    return this.post('/api/agent/content-plan/author/scrape', {
      ...request,
      targetAudienceIds: request.targetAudienceIds ?? [],
      locale: request.locale ?? 'en',
    });
  }

  /**
   * POST /api/agent/content-plan/author/analyze
   *
   * Re-analyze the existing scraped corpus — re-derive voice/playbook/post
   * examples without scraping. Used after manual corpus edits or when the
   * mapping prompt changes.
   */
  async authorAnalyze(request: {
    projectId: string;
    userId: string;
    existingAuthorId: string;
    platformId?: string;
    targetAudienceIds?: string[];
    locale?: string;
    /**
     * Schema 1.3.1+ — narrows AI re-derivation:
     *   'all'      — legacy (voice + identity + pillars + examples)
     *   'voice'    — only PlatformVoice + post_examples for the platform
     *   'identity' — only author-level positioning/expertise/originStory/values/UVP
     *   'pillars'  — only topic_pillars/*
     *   'examples' — only post_examples/*
     * Default 'all'.
     */
    scope?: 'all' | 'voice' | 'identity' | 'pillars' | 'examples';
    /** Override AI model. Default gemini-3-flash-preview (handler-side). */
    model?: string;
    /**
     * Optional user-supplied focus for THIS analysis pass — spliced into
     * the AI prompt as a `## User-provided analysis focus` section.
     * Empty/whitespace → no-op.
     */
    analysisInstructions?: string;
    /**
     * B3 (M3 Run-all autonomy): auto-confirm pillar drafts whose emitted
     * confidenceScore is STRICTLY ABOVE the project threshold. Default
     * false — pillars stay drafts for user review (D1).
     */
    autoConfirmPillars?: boolean;
  }): Promise<{ success: boolean; taskId?: string; error?: string }> {
    return this.post('/api/agent/content-plan/author/analyze', {
      ...request,
      manualInput: request.platformId
        ? { platformId: request.platformId, postTexts: [] }
        : undefined,
      targetAudienceIds: request.targetAudienceIds ?? [],
      locale: request.locale ?? 'en',
    });
  }

  /**
   * POST /api/agent/content-plan/pillars/:pillarId/reanalyze-sources
   *
   * Incremental, cheap pillar refresh. Backend stores only a proposal on the
   * backend task result; the caller decides whether to apply selected fields
   * into the open form and Save later.
   */
  async reanalyzePillarSources(request: {
    projectId: string;
    userId: string;
    authorId: string;
    pillarId: string;
    currentPillar: PillarSourceReanalysisCurrentPillar;
    newSourcePosts: Array<{ postId: string; platformId?: string; preview?: string }>;
    updatePolicy?: {
      mode?: PillarSourceReanalysisUpdateMode;
      existingSourcePostCount?: number;
      newlyAddedPostCount?: number;
      maxNewTheses?: number;
    };
    model?: string;
  }): Promise<{ success: boolean; taskId?: string; error?: string }> {
    const { pillarId, ...body } = request;
    return this.post(
      `/api/agent/content-plan/pillars/${encodeURIComponent(pillarId)}/reanalyze-sources`,
      body,
    );
  }

  // ─── Stage C — Post generation ──────────────────────────────────

  async generatePosts(request: {
    projectId: string;
    userId: string;
    authorId: string;
    /** Optional (Sprint 16) — standalone topic generation runs without strategy. */
    strategyId?: string;
    /** Calendar publication slot. When present, backend locks generation to this slot. */
    publicationId?: string;
    scope: 'topic' | 'sprint' | 'strategy';
    topicIds?: string[];
    sprintId?: string;
    platforms?: string[];
    concurrency?: number;
    researchPackIds?: string[];
    nichePlaybookId?: string;
    /** Sprint 16 — count of variants per (topic, platform); default 1. */
    postsPerPlatform?: number;
    asSeries?: boolean;
    seriesCount?: number;
    seriesFlavor?: 'continuation' | 'counter_point' | 'how_to_sequence' | 'story_arc';
    intendedDestination?: 'sprint' | 'topic_drafts' | 'post_examples';
    // Sprint 16 Part C — asset pipeline flags.
    generateAssetsImmediately?: boolean;
    skipAssetPlanning?: boolean;
    userInstruction?: string;
    planningMode?: 'auto' | 'manual';
    provider?: string;
    models?: {
      platformPost?: string;
      assetPlanner?: string;
      imageGen?: string;
    };
    /**
     * Task 4 — granular research-to-post selector. Map keyed by topicId so
     * batches can scope each topic's research independently.
     */
    researchSelectionByTopic?: Record<string, Array<{
      runId: string;
      includeAnalysisFields?: Array<
        'keyFacts' | 'contentSeeds' | 'quotes' | 'insightCandidates' | 'contradictions'
      >;
      includeChunkIds?: number[];
      includeFullResearch?: boolean;
    }>>;
    /** Task 7 — Insight Fund items attached for grounding. */
    insightFundItems?: Array<{ id: string; content: string; tags?: string[] }>;
    /**
     * Phase 2.1 (grid model) — per-cell scoping for the topic-detail grid.
     * When present, orchestrator narrows pair fan-out to a single
     * (topic, aspect, platform) bundle expanded by mode-specific count.
     * Legacy callers (TopicPostGenerationDialog, sprint apply, batch
     * /generate-posts) keep their existing fan-out by omitting cellScope.
     */
    cellScope?: {
      aspectKey?: string | null;
      platform: string;
      platforms?: string[];
      cellMode: 'single' | 'variants' | 'series' | 'fresh-variants';
      method?: 'cope' | 'direct';
      assignedGoal?: string;
      templateId?: string;
      formatId?: string;
      assetIntent?: 'text_only' | 'hero_image' | 'carousel' | 'video_cover' | 'native_document' | 'auto';
      lengthBucket?: 'short' | 'medium' | 'long';
      templateBindings?: Record<string, string>;
      templateIds?: string[];
      seriesCount?: number;
      seriesFlavor?: 'continuation' | 'counter_point' | 'how_to_sequence' | 'story_arc';
      freshVariantsCount?: number;
    };
    /**
     * Phase 2.1 — write-target switch. 'auto_apply_*' bypasses the draft
     * subcollection and writes straight to content_posts/*, the canonical
     * surface the topic-detail grid renders. 'draft_review' (default for
     * legacy callers) preserves the old draft-review flow.
     */
    applyMode?: 'auto_apply_topic' | 'auto_apply_sprint' | 'draft_review';
  }): Promise<{ success: boolean; taskId: string; error?: string }> {
    return this.post('/api/agent/content-plan/generate-posts', request);
  }

  async refineContentPost(request: {
    projectId: string;
    userId: string;
    postId: string;
    userPrompt: string;
    model?: string;
    authorId?: string;
  }): Promise<{ success: boolean; post?: Record<string, unknown>; error?: string }> {
    const { postId, ...body } = request;
    return this.post(`/api/agent/content-plan/posts/${encodeURIComponent(postId)}/refine`, body);
  }

  /**
   * Generate `topic.postBreakdown[]` aspects for ONE existing topic on demand.
   * Backend (`AspectGeneratorHandler`) writes the result directly to the
   * topic doc — Firestore subscription on `topic_backlog/{topicId}` propagates
   * the change to PostBreakdownEditor automatically.
   *
   * @param userInstruction Single-topic dialog only. Bulk-aspects flow MUST
   *                        pass undefined (per product policy — bulk has no
   *                        steering hint).
   */
  async generateAspects(request: {
    projectId: string;
    userId: string;
    topicId: string;
    /** 2..8 NEW aspects. Backend clamps to range. */
    count: number;
    model?: string;
    userInstruction?: string;
    /** Default true. False suppresses author block in prompt. */
    includeAuthor?: boolean;
    authorId: string;
    /**
     * Restrict pillar context. Empty array = explicit opt-out.
     * Absent = include every pillar referenced by the topic.
     */
    selectedPillarIds?: string[];
    /**
     * 'append' (DEFAULT) — generate `count` NEW aspects that complement the
     *   topic's existing `postBreakdown`. Final array is `[...existing, ...new]`.
     *   The existing aspects are surfaced to the AI prompt under "## Existing
     *   aspects (do NOT duplicate)" so it generates non-overlapping angles.
     * 'replace' — discard existing aspects, write only the freshly generated
     *   `count`. Use only when the user explicitly opts in via the dialog.
     */
    mode?: 'append' | 'replace';
  }): Promise<{ success: boolean; taskId: string; error?: string }> {
    return this.post(
      `/api/agent/content-plan/topics/${request.topicId}/generate-aspects`,
      {
        projectId: request.projectId,
        userId: request.userId,
        authorId: request.authorId,
        count: request.count,
        model: request.model,
        userInstruction: request.userInstruction,
        includeAuthor: request.includeAuthor,
        selectedPillarIds: request.selectedPillarIds,
        mode: request.mode,
      },
    );
  }

  // ─── Image generation for post examples + posts (Sprint 16) ────

  /**
   * Kick off an image generation task for a content-plan owner. Same backend
   * endpoint as screens — controller infers owner scope from `ownerKind` +
   * `ownerId`.
   */
  async generateImage(request: {
    projectId: string;
    userId: string;
    /**
     * Identifies which generated/applied Firestore doc owns the asset slot.
     */
    ownerKind: ContentPlanAssetOwnerKind;
    ownerId: string;
    slotKey: string;
    experimentId: string;
    prompt: string;
    negativePrompt?: string;
    aspectRatio?: string;
    imageSize?: string;
    style?: string;
    model?: string;
    referenceImages?: Array<{
      url: string;
      mimeType: string;
      name?: string;
      role?: string;
      instruction?: string;
    }>;
  }): Promise<{ success: boolean; taskId: string; error?: string }> {
    return this.post('/api/agent/generate-image', request);
  }

  // ─── Standalone post-asset planner (Sprint 16 Part C) ────────────

  /**
   * Plan (and optionally generate) assets for a post that's already been
   * created. Fire-and-forget — returns `{ taskId }` immediately. Backend
   * writes planned slots back to the owning post doc; when
   * mode='plan_and_generate' it also dispatches Gemini image gen per slot
   * and flips `status=completed + generatedUrl` on each experiment as the
   * images land.
   */
  async planPostAssets(request: {
    projectId: string;
    userId: string;
    authorId: string;
    postId: string;
    /** Optional freeform override of the planner's platform defaults. */
    userInstruction?: string;
    mode?: 'plan_only' | 'plan_and_generate';
    model?: string;
    imageGenModel?: string;
    /** Image-generation provider override. Inferred from imageGenModel when omitted. */
    imageGenProvider?: 'google' | 'openai';
    /** OpenAI-only quality tier. Ignored by Gemini. */
    imageGenQuality?: 'low' | 'medium' | 'high' | 'auto';
    /** OpenAI-only explicit size. Ignored by Gemini. */
    imageGenOpenaiSize?: string;
  }): Promise<{ success: boolean; taskId: string; error?: string }> {
    const { postId, ...body } = request;
    return this.post(
      `/api/agent/content-plan/posts/${encodeURIComponent(postId)}/plan-assets`,
      body,
    );
  }

  // ─── Design asset brief (v1.8.0) ──────────────────────────────────

  /**
   * Re-generate the DesignAssetBrief for an existing post WITHOUT
   * touching the writer text. Returns immediately with `taskId`.
   * Backend downgrades requested `text_only` to `plan_only` for this
   * endpoint (regenerating a brief implies plan_only at minimum).
   */
  async regenerateBrief(request: {
    projectId: string;
    userId: string;
    authorId: string;
    postId: string;
    assetMode?: 'plan_only' | 'plan_and_generate' | 'text_only';
    model?: string;
    imageGenModel?: string;
    imageGenProvider?: 'google' | 'openai';
    imageGenQuality?: 'low' | 'medium' | 'high' | 'auto';
    imageGenOpenaiSize?: string;
  }): Promise<{ success: boolean; taskId: string; error?: string }> {
    const { postId, ...body } = request;
    return this.post(
      `/api/agent/content-plan/posts/${encodeURIComponent(postId)}/regenerate-brief`,
      body,
    );
  }

  /**
   * Full chain re-run for an existing post: writer + validating + design
   * sub-pipeline + assets. Master regen marks dependent derivatives
   * with `staleSinceMasterRegen` so the UI can show a stale badge.
   */
  async regeneratePost(request: {
    projectId: string;
    userId: string;
    authorId: string;
    postId: string;
    assetMode?: 'plan_only' | 'plan_and_generate' | 'text_only';
    model?: string;
  }): Promise<{ success: boolean; taskId: string; error?: string }> {
    const { postId, ...body } = request;
    return this.post(
      `/api/agent/content-plan/posts/${encodeURIComponent(postId)}/regenerate-post`,
      body,
    );
  }

  /**
   * Read-only resolver for the env-driven default asset mode the bulk
   * strategy/sprint generation dialog should pre-select.
   */
  async getAssetModeDefault(): Promise<{
    success: boolean;
    mode: 'plan_only' | 'plan_and_generate' | 'text_only';
    source: 'explicit' | 'legacy_flags' | 'env' | 'fallback';
  }> {
    return this.get('/api/agent/content-plan/asset-mode-default');
  }

  // ─── Per-topic Deep Research (Sprint 16) ─────────────────────────

  /**
   * Trigger a topic-scoped Deep Research run. Fire-and-forget — returns
   * `{ taskId }` immediately. On completion the backend appends the new
   * research pack id to `topic.groundingResearchPackIds[]` atomically.
   */
  async runTopicDeepResearch(request: {
    projectId: string;
    userId: string;
    authorId: string;
    topicId: string;
    queries?: string[];
    sources?: unknown[];
    freshnessWindowDays?: number;
    maxResultsPerQuery?: number;
    model?: string;
    searchProvider?: 'auto' | 'perplexity' | 'gemini' | 'openai';
    researchDepth?: 'standard' | 'deep';
    reasoningEffort?: 'low' | 'medium' | 'high';
  }): Promise<{ success: boolean; taskId: string; error?: string }> {
    const { topicId, ...body } = request;
    return this.post(
      `/api/agent/content-plan/topics/${encodeURIComponent(topicId)}/deep-research`,
      body,
    );
  }

  // ─── Theme catalog — AI-assisted seeding ──────────────────────────

  /**
   * Kick off AI theme generation as a background task.
   *
   * Fire-and-forget (Sprint 14): returns `{ success, taskId }` immediately —
   * the backend writes phases via PhaseTracker + persists generated themes
   * into `catalogs/theme_catalog` when done. Frontend subscribes via
   * `useBackendTask(projectId, taskId)` for progress + `useThemeCatalog` for
   * the live-updated theme list.
   */
  async generateThemes(request: {
    projectId: string;
    userId: string;
    sourceData: unknown;
    /** Optional — backend records which entities the user picked; not required to run */
    sourceDataIds?: {
      audienceIds?: string[];
      taskIds?: string[];
      studyCaseIds?: string[];
      reviewIds?: string[];
      ratingIds?: string[];
      bigNumberIds?: string[];
      insightIds?: string[];
      insightsAnalysisId?: string;
    };
    existingThemeIds?: string[];
    count?: number;
    projectContext?: string;
    userHint?: string;
    models?: { themeGenerator?: string };
    provider?: string;
  }): Promise<{ success: boolean; taskId: string; error?: string }> {
    return this.post('/api/agent/content-plan/generate-themes', request);
  }

  // ─── Review-and-apply (Sprint 15) ────────────────────────────────

  /**
   * Promote accepted theme drafts from a generation run into canonical
   * `catalogs/theme_catalog.themes[]`.
   */
  async applyThemes(request: {
    projectId: string;
    generationId: string;
    acceptedItemIds: string[];
  }): Promise<{ success: boolean; appliedCount: number; skippedCount: number; rejectedCount: number; error?: string }> {
    const { generationId, ...body } = request;
    return this.post(`/api/agent/content-plan/themes/${encodeURIComponent(generationId)}/apply`, body);
  }

  /**
   * Promote accepted topic drafts into `topic_backlog/{id}` with status='ready'.
   */
  async applyTopics(request: {
    projectId: string;
    generationId: string;
    authorId: string;
    acceptedItemIds: string[];
  }): Promise<{ success: boolean; appliedCount: number; skippedCount: number; rejectedCount: number; error?: string }> {
    const { generationId, ...body } = request;
    return this.post(`/api/agent/content-plan/topics/${encodeURIComponent(generationId)}/apply`, body);
  }

  /**
   * Promote accepted post drafts into `sprints/{sprintId}/posts/`. Schedule
   * (scheduledAt) is computed server-side at apply time.
   */
  async applyPosts(request: {
    projectId: string;
    generationId: string;
    authorId: string;
    acceptedItemIds: string[];
    /**
     * Sprint 17 — choose the apply destination. `'sprint'` (default) promotes
     * drafts to the sprint's canonical posts subcollection. `'topic'` keeps
     * drafts in place (marked accepted) and links their IDs onto the topic's
     * `usedInPostIds`, so users can save posts generated from a standalone
     * topic without needing a strategy/sprint.
     */
    scope?: 'sprint' | 'topic';
    /** Required when scope='sprint'. */
    sprintId?: string;
    /** Required when scope='topic'. */
    topicId?: string;
  }): Promise<{ success: boolean; appliedCount: number; skippedCount: number; rejectedCount: number; error?: string }> {
    const { generationId, ...body } = request;
    return this.post(`/api/agent/content-plan/posts/${encodeURIComponent(generationId)}/apply`, body);
  }

  // ─── Stage D — Creator Research refresh ──────────────────────────

  async creatorResearchRefresh(request: {
    projectId: string;
    userId: string;
    creatorReferenceIds?: string[];
    budgetCentsUsd?: number;
    minNewPostsThreshold?: number;
    reaggregateThreshold?: number;
    concurrency?: number;
  }): Promise<{ success: boolean; taskId: string; error?: string }> {
    return this.post('/api/agent/content-plan/creator-research/refresh', request);
  }

  /**
   * Bootstrap new creators into a project and scrape their latest posts.
   * Creates `creator_references` docs for the provided handles, then
   * delegates to the refresh orchestrator via `creatorReferenceIds` filter.
   *
   * Handles are normalised server-side (trimmed, `@`-stripped, lowercased).
   * Max 15 handles per call (matches `maxCreatorsPerResearch`).
   */
  async creatorResearchInit(request: {
    projectId: string;
    userId: string;
    handles: string[];
    platform: PlatformId;
    refreshCadenceDays?: number;
  }): Promise<{ success: boolean; taskId?: string; createdCreatorIds?: string[]; error?: string }> {
    return this.post('/api/agent/content-plan/creator-research/init', request);
  }

  /**
   * AI-powered creator discovery. Given a platform + optional keywords +
   * project context (loaded server-side from audiences/insights/tasks),
   * Gemini returns a ranked list of candidate creators with rationale.
   *
   * Synchronous — returns candidates in the response (5-15s typical).
   * No task doc — UI shows inline loading state.
   */
  /**
   * Start an AI creator discovery run. Fire-and-forget: returns runId + taskId
   * immediately, Gemini 2-pass work happens in the background. Subscribe to
   * `creator_generations/{runId}/generatedCreators` for live candidates and to
   * `backend_tasks/{taskId}` for phase progress.
   */
  async creatorResearchDiscover(request: {
    projectId: string;
    userId: string;
    platform: PlatformId;
    keywords?: string;
    maxResults?: number;
    /**
     * Which search provider to use:
     *  - 'auto' (default) — Perplexity when its key is configured, Gemini fallback.
     *  - 'perplexity'    — force Perplexity; backend returns 400 with
     *                      errorCode='missing_perplexity_key' when no key.
     *  - 'gemini'        — force Gemini grounded search.
     *  - 'openai'        — force OpenAI Responses API grounded search.
     */
    searchProvider?: 'auto' | 'perplexity' | 'gemini' | 'openai';
  }): Promise<{
    success: boolean;
    /** Generation run id — persisted at projects/{pid}/creator_generations/{runId} */
    runId?: string;
    /** Backend task id — progress chip via backend_tasks/{taskId}. */
    taskId?: string;
    error?: string;
    errorCode?: string;
  }> {
    return this.post('/api/agent/content-plan/creator-research/discover', request);
  }

  /**
   * Apply a creator discovery run: accepted candidates become
   * `creator_references` docs and are immediately scraped via Apify.
   *
   * Mirrors the apply-pattern used for theme/topic/post generations.
   */
  async applyCreatorDiscovery(request: {
    projectId: string;
    userId: string;
    generationId: string;
    acceptedItemIds: string[];
  }): Promise<{
    success: boolean;
    appliedCount?: number;
    skippedCount?: number;
    rejectedCount?: number;
    createdIds?: string[];
    scrapeTaskId?: string;
    error?: string;
  }> {
    const { generationId, ...body } = request;
    return this.post(
      `/api/agent/content-plan/creator/${encodeURIComponent(generationId)}/apply`,
      body,
    );
  }

  // ─── Sprint 18 — Three-phase topic research pipeline ──────────────

  /**
   * POST brief — Phase 1 compose OR recompose.
   *
   * Omit `runId` to create a fresh research run envelope and start Phase 1.
   * Pass an existing `runId` to recompose the brief in place: same envelope,
   * `run.brief` is overwritten, history list stays stable. This is how the
   * UI avoids spawning a new run every time the user tweaks queries.
   */
  async composeTopicResearchBrief(request: {
    projectId: string;
    userId: string;
    authorId: string;
    topicId: string;
    preferredSources?: string[];
    negativeSources?: string[];
    models?: { brief?: string; research?: string | null; analysis?: string };
    searchProvider?: 'auto' | 'perplexity' | 'gemini' | 'openai';
    researchDepth?: 'standard' | 'deep';
    reasoningEffort?: 'low' | 'medium' | 'high';
    title?: string;
    focus?: {
      kind: 'topic' | 'aspect' | 'custom';
      label: string;
      aspectKey?: string;
      aspectAngle?: string;
      goal?: string;
    };
    userHints?: string;
    /** Paths selected manually via CompositeDataPicker. */
    manualSelectionPaths?: string[];
    hydratedData?: unknown;
    /** When set, reuse this existing run (recompose brief in place). */
    runId?: string;
  }): Promise<{ success: boolean; runId?: string; error?: string }> {
    const { topicId, ...body } = request;
    return this.post(
      `/api/agent/content-plan/topics/${encodeURIComponent(topicId)}/research/brief`,
      body,
    );
  }

  /** PATCH the brief between Phase 1 and 2 (user edits queries/sources/etc). */
  async patchResearchBrief(request: {
    projectId: string;
    authorId: string;
    runId: string;
    patch: Record<string, unknown>;
  }): Promise<{ success: boolean; brief?: unknown; error?: string }> {
    const { runId, ...body } = request;
    return this.patch(
      `/api/agent/content-plan/research/${encodeURIComponent(runId)}/brief`,
      body,
    );
  }

  /** Mark/unmark a completed research study as active for post generation defaults. */
  async setResearchRunActive(request: {
    projectId: string;
    authorId: string;
    runId: string;
    active: boolean;
  }): Promise<{ success: boolean; run?: unknown; error?: string }> {
    const { runId, ...body } = request;
    return this.patch(
      `/api/agent/content-plan/research/${encodeURIComponent(runId)}/active`,
      body,
    );
  }

  /**
   * POST Phase 2 — consumes /brief/current, runs grounded search.
   *
   * Optional `model` overrides `run.modelPreferences.research` for this
   * specific re-run. Backend rejects with HTTP 409 when the research phase
   * is currently running (mid-run race guard).
   */
  async executeResearch(request: {
    projectId: string;
    authorId: string;
    userId?: string;
    runId: string;
    /** Override `run.modelPreferences.research` for this re-run. */
    model?: string | null;
    searchProvider?: 'auto' | 'perplexity' | 'gemini' | 'openai';
    researchDepth?: 'standard' | 'deep';
    reasoningEffort?: 'low' | 'medium' | 'high';
  }): Promise<{ success: boolean; runId?: string; error?: string }> {
    const { runId, ...body } = request;
    return this.post(
      `/api/agent/content-plan/research/${encodeURIComponent(runId)}/execute`,
      body,
    );
  }

  /**
   * POST Phase 3 — consumes /raw_research/current, structures analysis.
   *
   * Optional `model` overrides `run.modelPreferences.analysis` for this
   * specific re-run. Backend rejects with HTTP 409 when the analysis phase
   * is currently running (mid-run race guard).
   */
  async analyseResearch(request: {
    projectId: string;
    authorId: string;
    userId?: string;
    runId: string;
    /** Override `run.modelPreferences.analysis` for this re-run. */
    model?: string;
  }): Promise<{ success: boolean; runId?: string; error?: string }> {
    const { runId, ...body } = request;
    return this.post(
      `/api/agent/content-plan/research/${encodeURIComponent(runId)}/analyse`,
      body,
    );
  }

  /** POST run-all — orchestrator runs Phase 1 + 2 + 3 sequentially. */
  async runAllTopicResearch(request: {
    projectId: string;
    userId: string;
    authorId: string;
    topicId: string;
    preferredSources?: string[];
    negativeSources?: string[];
    models?: { brief?: string; research?: string | null; analysis?: string };
    searchProvider?: 'auto' | 'perplexity' | 'gemini' | 'openai';
    researchDepth?: 'standard' | 'deep';
    reasoningEffort?: 'low' | 'medium' | 'high';
    title?: string;
    focus?: {
      kind: 'topic' | 'aspect' | 'custom';
      label: string;
      aspectKey?: string;
      aspectAngle?: string;
      goal?: string;
    };
    userHints?: string;
  }): Promise<{ success: boolean; runId?: string; error?: string }> {
    const { topicId, ...body } = request;
    return this.post(
      `/api/agent/content-plan/topics/${encodeURIComponent(topicId)}/research/run-all`,
      body,
    );
  }

  /** DELETE cascades the run envelope + all subcollection docs. */
  async deleteResearchRun(request: {
    projectId: string;
    authorId: string;
    runId: string;
  }): Promise<{ success: boolean; error?: string }> {
    const url = `/api/agent/content-plan/research/${encodeURIComponent(request.runId)}?projectId=${encodeURIComponent(request.projectId)}&authorId=${encodeURIComponent(request.authorId)}`;
    return this.delete(url);
  }

  // ─── HTTP helpers ─────────────────────────────────────────────────

  private async get<T>(path: string): Promise<T> {
    return this.request('GET', path);
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    return this.request('POST', path, body);
  }

  private async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request('PATCH', path, body);
  }

  private async delete<T>(path: string): Promise<T> {
    return this.request('DELETE', path);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const hasBody = body !== undefined;
    // Only set Content-Type when actually sending a body. Edge-gateway
    // (Fastify) rejects Content-Type: application/json + empty body with
    // "Unexpected end of JSON input" 500s — hence the guard.
    const headers: Record<string, string> = hasBody
      ? { 'Content-Type': 'application/json' }
      : {};
    const token = await this.resolveAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: hasBody ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let payload: Record<string, unknown> | undefined;
      try {
        payload = text ? JSON.parse(text) : undefined;
      } catch {
        payload = undefined;
      }
      const blocked = parseBillingBlockedPayload(response.status, payload);
      if (blocked) {
        const projectId = typeof (body as any)?.projectId === 'string' ? (body as any).projectId : undefined;
        emitBillingBlocked(blocked.billingBlock, { projectId, actionLabel: path });
        throw blocked;
      }
      throw new Error(`ContentPlanApiClient: ${response.status} ${response.statusText} — ${text}`);
    }
    // Guard against a 2xx HTML response (e.g. SPA fallback when the request
    // reached the frontend origin instead of the API). Without this, a JSON
    // parse failure surfaces as "Unexpected token '<', '<!DOCTYPE...'" with
    // no indication of the actual wiring problem.
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      const preview = (await response.text().catch(() => '')).slice(0, 120);
      throw new Error(
        `ContentPlanApiClient: non-JSON ${response.status} response from ${path} ` +
          `(content-type=${contentType || 'missing'}, body: ${preview})`,
      );
    }
    return response.json() as Promise<T>;
  }

  private async requestJsonAllowHttpError<T>(method: string, path: string, body?: unknown): Promise<T> {
    const hasBody = body !== undefined;
    const headers: Record<string, string> = hasBody
      ? { 'Content-Type': 'application/json' }
      : {};
    const token = await this.resolveAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: hasBody ? JSON.stringify(body) : undefined,
    });
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      const payload = await response.json() as Record<string, unknown>;
      const blocked = parseBillingBlockedPayload(response.status, payload);
      if (blocked) {
        const projectId = typeof (body as any)?.projectId === 'string' ? (body as any).projectId : undefined;
        emitBillingBlocked(blocked.billingBlock, { projectId, actionLabel: path });
      }
      return payload as T;
    }
    const text = await response.text().catch(() => '');
    return {
      success: false,
      error: `ContentPlanApiClient: ${response.status} ${response.statusText} — ${text}`,
    } as T;
  }

  private async resolveAuthToken(): Promise<string | null> {
    if (this.getAuthToken) {
      return this.getAuthToken();
    }
    return getAuth().currentUser?.getIdToken() ?? null;
  }
}
